import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../constants/bucket';
import {
  IItemSourceEvidence,
  IMarketplaceMarkedItem,
  IMarketplaceSendItem,
  IMarketplaceSendSource,
  MARKETPLACE_CAMPAIGN_ID,
  MARKETPLACE_FULFILLMENT_CENTER_ID,
  MARKETPLACE_PRODUCT_ID_PREFIX,
} from '../types/marketplace';

export interface ISplitPickCampaignIds {
  campaignIds: string[];
  marketplaceIds: string[];
}

export const isMarketplaceOptionId = (id: string | null | undefined): boolean =>
  typeof id === 'string' && id.trim().toLowerCase().startsWith(MARKETPLACE_PRODUCT_ID_PREFIX);

export const splitPickCampaignIds = (ids: readonly string[] | null | undefined): ISplitPickCampaignIds =>
  (ids ?? []).reduce<ISplitPickCampaignIds>(
    (acc, id) => {
      if (isMarketplaceOptionId(id)) {
        acc.marketplaceIds.push(id);
      } else {
        acc.campaignIds.push(id);
      }

      return acc;
    },
    { campaignIds: [], marketplaceIds: [] },
  );

export const getMarketplacePickOptions = (send: IMarketplaceSendSource | null | undefined): IMarketplaceSendItem[] => {
  const map = send?.marketplace;

  if (!map) {
    return [];
  }

  const { marketplaceIds } = splitPickCampaignIds(send?.pick_campaign_ids);

  return marketplaceIds.map((id) => map[id]).filter((item): item is IMarketplaceSendItem => !!item);
};

export const getMarketplaceCartItems = (send: IMarketplaceSendSource | null | undefined): IMarketplaceSendItem[] => {
  const map = send?.marketplace;

  if (!map) {
    return [];
  }

  const pickedIds = new Set(send?.pick_campaign_ids ?? []);

  return Object.keys(map)
    .filter((key) => !pickedIds.has(key))
    .map((key) => map[key])
    .filter((item): item is IMarketplaceSendItem => !!item);
};

export const hasMarketplacePickOptions = (send: IMarketplaceSendSource | null | undefined): boolean =>
  getMarketplacePickOptions(send).length > 0;

export const isMarketplaceCampaignId = (id: string | null | undefined): boolean =>
  typeof id === 'string' && (id.trim() === MARKETPLACE_CAMPAIGN_ID || isMarketplaceOptionId(id));

export const isRealCampaignId = (id: string | null | undefined): boolean => {
  const trimmed = typeof id === 'string' ? id.trim() : '';

  return trimmed !== '' && trimmed !== CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID && !isMarketplaceCampaignId(trimmed);
};

export const isMarketplaceEngagementItem = (item: IMarketplaceMarkedItem | null | undefined): boolean =>
  !!item && (item.fulfillment_center_id === MARKETPLACE_FULFILLMENT_CENTER_ID || isMarketplaceOptionId(item.item_id));

export const getWarehouseItems = <T extends IMarketplaceMarkedItem>(items: readonly T[] | null | undefined): T[] =>
  (items ?? []).filter((item) => !isMarketplaceEngagementItem(item));

export const offersMarketplaceContent = (send: IItemSourceEvidence | null | undefined): boolean => {
  if (!send) {
    return false;
  }

  if (send.marketplace && Object.keys(send.marketplace).length > 0) {
    return true;
  }

  if (send.gift_budget) {
    return true;
  }

  return (send.pick_campaign_ids ?? []).some(isMarketplaceOptionId);
};

export const getMarketplaceSendCards = (
  send: IItemSourceEvidence | null | undefined,
  isPickOptions: boolean,
): IMarketplaceSendItem[] => {
  const map = send?.marketplace;

  if (!map) {
    return [];
  }

  const claimedIds = new Set(
    (send?.items ?? [])
      .filter(isMarketplaceEngagementItem)
      .map((item) => item.item_id)
      .filter((id): id is string => !!id),
  );

  if (claimedIds.size > 0) {
    const claimed = Object.keys(map)
      .filter((key) => claimedIds.has(key))
      .map((key) => map[key])
      .filter((item): item is IMarketplaceSendItem => !!item);

    return claimed.length ? claimed : Object.values(map).filter((item): item is IMarketplaceSendItem => !!item);
  }

  return isPickOptions ? getMarketplacePickOptions(send) : getMarketplaceCartItems(send);
};
